import { usePaginatedVariables } from "hooks/strings/usePaginatedVariables";
import usePaginatedResources from "hooks/usePaginatedResources";
import { useCallback, useMemo, useEffect } from "react";
import useGetDailyFormWorkOrdersForTable from "./useGetDailyFormWorkOrdersForTable";
import dayjs from "dayjs";
import { capitalCase } from "change-case";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import { useNavigate } from "react-router-dom";
import WorkOrderActionsCell from "modules/fleet/WorkOrderActionsCell/WorkOrderActionsCell";
import WorkOrderStatusCell from "modules/fleet/WorkOrderStatusCell/WorkOrderStatusCell";
import {
  DailyFormIssuePriority,
  DailyFormIssueStatus,
  SortDirection,
} from "generated/graphql";
import { sortDirectionMap } from "constants/sortDirectionMap";
import MultipleChoiceFilter from "components/filters/MultipleChoiceFilter/MultipleChoiceFilter";
import DateFilter from "components/filters/DateFilter/DateFilter";
import { enumToValueOptions } from "utils/enums/enumToValueOptions";
import useGetAllOrganizationUsersNonEmployee from "hooks/users/useGetAllOrganizationUsersNonEmployee";
import useGetDailyFormVehicles from "./useGetDailyFormVehicles";
import { activeWorkOrderStatuses, workOrderStatusOptions } from "modules/fleet/workOrderStatusOptions";

export const workOrdersSortKeyMap = {
  id: "ID",
  issueName: "ISSUE_NAME",
  "vehicle.name": "VEHICLE",
  "user.name": "SUBMITTED_BY",
  priority: "PRIORITY",
  status: "STATUS",
  "assignedTo.name": "ASSIGNED_TO",
  neededCompletionDate: "NEEDED_COMPLETION_DATE",
  completedAt: "COMPLETED_AT",
  createdAt: "CREATED_AT",
};

export const workOrdersInitialSortDirectionKey = "desc";
export const workOrdersInitialSortDirectionValue = "DESC";
export const workOrdersInitialSortKeyKey = "createdAt";
export const workOrdersInitialSortKeyValue = "CREATED_AT";

const priorityBadgeColorMap = {
  [DailyFormIssuePriority.HIGH]: "error",
  [DailyFormIssuePriority.MEDIUM]: "warning",
  [DailyFormIssuePriority.LOW]: "success",
} as const;

export default function useWorkOrdersTable() {
  const navigate = useNavigate();

  const { filtering, sorting, offset } = usePaginatedVariables({
    initialBillingStatuses: activeWorkOrderStatuses,
  });

  const params = {
    page: offset.page,
    first: offset.first,
    search: filtering.debouncedSearchTerm,
    sortDirection: sortDirectionMap[sorting.sortDirection] || workOrdersInitialSortDirectionValue,
    sortKey: workOrdersSortKeyMap[sorting.sortKey] || workOrdersInitialSortKeyValue,
    multiplePriorities: filtering.statuses as DailyFormIssuePriority[],
    multipleStatuses: filtering.billingStatuses as DailyFormIssueStatus[],
    multipleAssignedTo: filtering.multipleUserIDs,
    neededCompletionDate: filtering.dueAt,
    completedDate: filtering.completedAt,
    vehicleId: filtering.vehicleId || undefined,
  };

  const { data, loading, error, paginatorInfo } = useGetDailyFormWorkOrdersForTable({
    variables: params,
  });

  const {
    loading: organizationUsersLoading,
    data: organizationUsersData,
    error: organizationUsersError,
  } = useGetAllOrganizationUsersNonEmployee();
  const [getDailyFormVehicles, { data: vehiclesData }] = useGetDailyFormVehicles();

  useEffect(() => {
    getDailyFormVehicles({ first: 1000, page: 1 });
  }, [getDailyFormVehicles]);

  const pagination = usePaginatedResources({
    paginatorInfo,
    paginate: offset.paginate,
  });

  const handlePriorityChecked = useCallback(
    (selectedOptions) => {
      return filtering.setStatuses(selectedOptions);
    },
    [filtering]
  );

  const handleStatusChecked = useCallback(
    (selectedOptions) => {
      return filtering.setBillingStatuses(selectedOptions);
    },
    [filtering]
  );

  const handleAssignedToChecked = useCallback(
    (selectedOptions) => {
      return filtering.setMultipleUserIDs(selectedOptions);
    },
    [filtering]
  );

  const handleVehicleChecked = useCallback(
    (selectedOptions) => {
      return filtering.setVehicleId(selectedOptions?.[0] ?? null);
    },
    [filtering]
  );

  const PriorityFilter = useCallback(
    ({ column: { id } }) => {
      return (
        <MultipleChoiceFilter
          id={id}
          value={filtering.statuses}
          options={enumToValueOptions(DailyFormIssuePriority)}
          onChange={handlePriorityChecked}
        />
      );
    },
    [filtering.statuses, handlePriorityChecked]
  );

  const StatusFilter = useCallback(
    ({ column: { id } }) => {
      return (
        <MultipleChoiceFilter
          id={id}
          value={filtering.billingStatuses}
          options={workOrderStatusOptions.map((option) => ({
            label: option.label,
            value: option.value,
          }))}
          onChange={handleStatusChecked}
        />
      );
    },
    [filtering.billingStatuses, handleStatusChecked]
  );

  const AssignedToFilter = useCallback(
    ({ column: { id } }) => {
      return (
        <MultipleChoiceFilter
          id={id}
          value={filtering.multipleUserIDs}
          loading={organizationUsersLoading}
          error={organizationUsersError}
          options={
            organizationUsersData?.map((user) => ({
              label: user.name,
              value: user.id,
            })) ?? []
          }
          onChange={handleAssignedToChecked}
        />
      );
    },
    [
      filtering.multipleUserIDs,
      organizationUsersData,
      organizationUsersLoading,
      organizationUsersError,
      handleAssignedToChecked,
    ]
  );

  const VehicleFilter = useCallback(
    ({ column: { id } }) => {
      return (
        <MultipleChoiceFilter
          id={id}
          value={filtering.vehicleId ? [filtering.vehicleId] : []}
          options={
            vehiclesData?.map((vehicle) => ({
              label: vehicle.name,
              value: vehicle.id,
            })) ?? []
          }
          onChange={handleVehicleChecked}
        />
      );
    },
    [filtering.vehicleId, vehiclesData, handleVehicleChecked]
  );

  const CompletionDateFilter = useCallback(
    ({ column: { id } }) => {
      return (
        <DateFilter
          id={id}
          value={filtering.dueAt}
          onChange={(selectedDate) =>
            filtering.setDueAt((prevState) => (prevState == selectedDate ? null : selectedDate))
          }
        />
      );
    },
    [filtering.dueAt]
  );

  const CompletedDateFilter = useCallback(
    ({ column: { id } }) => {
      return (
        <DateFilter
          id={id}
          value={filtering.completedAt}
          onChange={(selectedDate) =>
            filtering.setCompletedAt((prevState) => (prevState == selectedDate ? null : selectedDate))
          }
        />
      );
    },
    [filtering.completedAt]
  );

  const columns = useMemo(() => {
    return [
      {
        Header: "Issue Name",
        id: "issueName",
        accessor: "issueName",
        width: "20%",
        Cell: ({ value, row }: any) => (
          <MDTypography
            variant="body"
            color="info"
            sx={{
              cursor: "pointer",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
            onClick={() => navigate(`/fleet/work-orders/${row.original.id}/edit`)}
          >
            {value}
          </MDTypography>
        ),
      },
      {
        Header: "Vehicle",
        id: "vehicle.name",
        accessor: "vehicle.name",
        width: "12%",
        Filter: VehicleFilter,
      },
      { Header: "Submitted By", id: "user.name", accessor: "user.name", width: "12%" },
      {
        Header: "Priority",
        id: "priority",
        accessor: "priority",
        width: "10%",
        Filter: PriorityFilter,
        Cell: ({ value }: any) => {
          return (
            <MDBadge
              variant="contained"
              color={priorityBadgeColorMap[value] ?? "info"}
              size="sm"
              badgeContent={capitalCase(value)}
              container
            />
          );
        },
      },
      {
        Header: "Status",
        id: "status",
        accessor: "status",
        width: "15%",
        Filter: StatusFilter,
        Cell: ({ value, row }: any) => {
          return <WorkOrderStatusCell workOrderId={row.original.id} currentStatus={value} />;
        },
      },
      {
        Header: "Assigned To",
        id: "assignedTo.name",
        accessor: "assignedTo.name",
        width: "12%",
        Filter: AssignedToFilter,
      },
      {
        Header: "Due Date",
        id: "neededCompletionDate",
        accessor: "neededCompletionDate",
        width: "12%",
        Filter: CompletionDateFilter,
        Cell: ({ value }: any) => (value ? dayjs(value).format("MM/DD/YYYY") : "-"),
      },
      {
        Header: "Completed Date",
        id: "completedAt",
        accessor: "completedAt",
        width: "12%",
        Filter: CompletedDateFilter,
        Cell: ({ value }: any) => (value ? dayjs(value).format("MM/DD/YYYY") : "-"),
      },
      {
        Header: "Created",
        id: "createdAt",
        accessor: "createdAt",
        width: "10%",
        Cell: ({ value }: any) => dayjs(value).format("MM/DD/YYYY"),
      },
      {
        align: "center",
        Header: "Actions",
        accessor: "actions",
        width: "7%",
        disableSortBy: true,
        Cell: WorkOrderActionsCell,
      },
    ];
  }, [
    navigate,
    VehicleFilter,
    PriorityFilter,
    StatusFilter,
    AssignedToFilter,
    CompletionDateFilter,
    CompletedDateFilter,
  ]);

  const tableData = useMemo(() => {
    return {
      columns,
      rows: data || [],
    };
  }, [columns, data]);

  return {
    tableData,
    loading,
    error,
    pagination,
    filtering,
    sorting,
  } as const;
}
